import type { App, Plugin } from 'vue'
import { computed, inject, ref } from 'vue'
import * as signalR from '@microsoft/signalr'
import { useNetwork } from '@vueuse/core'

export const CHAT_HUB_KEY = Symbol('ChatHubService') // Injection key

export type ChatHubServiceType = ReturnType<typeof chatHubService>

function chatHubService() {
  const connection = ref<signalR.HubConnection | null>(null)
  const isConnected = computed(() => connection.value?.state === signalR.HubConnectionState.Connected)

  const { isOnline } = useNetwork()

  const waitForOnline = async () => {
    if (isOnline.value) {
      return
    }

    await new Promise<void>((resolve) => {
      const stop = watch(isOnline, (val) => {
        if (val) {
          stop()
          resolve()
        }
      })
    })
  }

  const reconnectHandlers: Record<string, () => void> = {}
  let reconnectAttempts = 0
  const maxReconnectAttempts = 5

  let lockResolver: (() => void) | null = null

  const acquireWebLock = () => {
    if (lockResolver) {
      console.info('[ChatHub] Lock already acquired.')

      return
    }

    if (navigator && 'locks' in navigator && typeof navigator.locks.request === 'function') {
      const promise = new Promise<void>((resolve) => {
        lockResolver = resolve
      })

      navigator.locks.request('chat_hub_lock', { mode: 'shared' }, () => promise)

      console.info('[ChatHub] Acquired web lock.')
    }
  }

  const releaseWebLock = () => {
    if (lockResolver) {
      lockResolver()
      lockResolver = null

      console.info('[ChatHub] Released web lock.')
    } else {
      console.info('[ChatHub] No web lock to release.')
    }
  }

  /**
   * Initializes the SignalR connection.
   */
  const init = async (accessTokenCallback: () => Promise<string>) => {
    if (connection.value) {
      return
    }

    connection.value = new signalR.HubConnectionBuilder()
      .configureLogging(
        !import.meta.env.PROD
          ? signalR.LogLevel.Trace
          : signalR.LogLevel.None,
      )
      .withUrl(`${import.meta.env.VITE_APP_API_URL}/hubs/chat`, {
        accessTokenFactory: accessTokenCallback,
      })
      .withAutomaticReconnect([
        500,
        1000,
        2000,
        5000,
        5000,
        5000,
        5000,
        5000,
      ])
      .build()

    setupEventListeners()
  }

  /**
   * Sets up event listeners for connection state changes.
   */
  const setupEventListeners = () => {
    if (!connection.value) {
      return
    }

    connection.value.onreconnecting(() => {
      console.info('[ChatHub] Reconnecting...')
      acquireWebLock()
    })

    connection.value.onreconnected(() => {
      console.info('[ChatHub] Reconnected successfully.')
      reconnectAttempts = 0

      // Execute all reconnect handlers
      for (const key in reconnectHandlers) {
        reconnectHandlers[key]()
      }
    })

    connection.value.onclose(async () => {
      console.info('[ChatHub] Connection closed.')

      if (reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++
        console.info(`[ChatHub] Retrying connection... Attempt ${reconnectAttempts}/${maxReconnectAttempts}`)
        await waitForOnline()
        setTimeout(start, 2000)
      } else {
        console.error('[ChatHub] Maximum reconnection attempts reached. Please check your connection.')
        releaseWebLock()
      }
    })
  }

  /**
   * Starts the SignalR connection.
   */
  const start = async () => {
    if (!connection.value || connection.value.state !== signalR.HubConnectionState.Disconnected) {
      return
    }

    try {
      await waitForOnline()
      acquireWebLock()
      await connection.value.start()
      console.info('[ChatHub] Connection started successfully.')
    } catch (error) {
      console.error('[ChatHub] Failed to start connection:', error)
    }
  }

  /**
   * Stops the SignalR connection.
   */
  const stop = async () => {
    if (!connection.value || connection.value.state === signalR.HubConnectionState.Disconnected) {
      return
    }

    try {
      releaseWebLock()
      await connection.value.stop()
      console.info('[ChatHub] Connection stopped successfully.')
    } catch (error) {
      console.error('[ChatHub] Error stopping connection:', error)
    }
  }

  /**
   * Registers a callback function to be executed when the connection is re-established.
   */
  const onReconnected = (key: string, method: () => void) => {
    reconnectHandlers[key] = method
  }

  /**
   * Registers a method for SignalR events.
   */
  const on = (methodName: string, method: (...args: any[]) => void) => {
    if (!connection.value) {
      return
    }

    connection.value.off(methodName) // Ensure no duplicate listeners
    connection.value.on(methodName, method)
  }

  const retryInvoke = async <T>(times: number, methodName: string, ...args: any[]) => {
    if (times > 3) {
      throw new Error(`[ChatHub] Error invoking method "${methodName}": Maximum retry attempts reached`)
    }

    if (!isOnline.value) {
      console.info(`[ChatHub] Offline. Waiting to invoke "${methodName}"`)
      await waitForOnline()
    }

    if (!connection.value || connection.value.state !== signalR.HubConnectionState.Connected) {
      console.info(`[ChatHub] Retry attempt ${times + 1}: Connection not ready`)

      await new Promise((resolve) => setTimeout(resolve, 1000))

      return await retryInvoke(times + 1, methodName, ...args)
    }

    try {
      return await connection.value.invoke<T>(methodName, ...args)
    } catch (error) {
      console.info(`[ChatHub] Error invoking method "${methodName}":`, error)
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return await retryInvoke(times + 1, methodName, ...args)
    }
  }

  /**
   * Invokes a method on the SignalR hub.
   */
  const invoke = async <T>(methodName: string, ...args: any[]): Promise<T> => {
    if (!connection.value) {
      throw new Error('[ChatHub] Connection is not initialized')
    }

    if (!isOnline.value) {
      console.info(`[ChatHub] Offline. Waiting to invoke "${methodName}"`)
      await waitForOnline()
    }

    if (connection.value.state !== signalR.HubConnectionState.Connected) {
      return await retryInvoke(0, methodName, ...args)
    }

    try {
      return await connection.value.invoke<T>(methodName, ...args)
    } catch (error) {
      console.error(`[ChatHub] Error invoking method "${methodName}":`, error)
      throw error
    }
  }

  return {
    init,
    start,
    stop,
    isConnected,
    onReconnected,
    on,
    invoke,
    connection,
  }
}

/**
 * Vue Plugin to install the ChatHubService globally.
 */
export default {
  install(app: App) {
    const service = chatHubService()

    app.provide(CHAT_HUB_KEY, service)
  },
} as Plugin

/**
 * Hook to use the ChatHubService in components.
 */
export function useChatHubService(): ChatHubServiceType {
  const service = inject(CHAT_HUB_KEY) as ChatHubServiceType
  if (!service) {
    throw new Error('ChatHubService is not provided')
  }

  return service
}
